import AddCircleIcon from '@mui/icons-material/AddCircle';
import MapIcon from '@mui/icons-material/Map';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllDevicesQuery } from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import { CustomSwitch } from '../../../layout/styledcomponents/CustomSwitch';
import DeviceMapView from '../../components/Vehicle Management/DeviceMapView';
import DeviceModal from '../../components/Vehicle Management/DeviceModal';
import GenericTable from '../../shared/GenericTable';
import { Device } from '../../types/vehicles';

export default function VehiclesPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(
        undefined,
    );

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [mapView, setMapView] = useState(false);

    const { data: devices, isLoading } = useGetAllDevicesQuery();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'uniqueId',
            header: 'Device Name',
            filterFn: 'fuzzy',
        },
        {
            accessorKey: 'module',
            header: 'Vehicle',
            accessorFn: (row) => row.module?.name,
        },
        {
            accessorKey: 'owner',
            header: 'Owner',
            accessorFn: (row) => row.owner?.name,
        },
        {
            accessorKey: 'enabled',
            header: 'Status',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return String(row.getValue('enabled')) === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return (
                    <CustomChip
                        style={{ cursor: 'pointer' }}
                        label={value === true ? 'Enabled' : 'Disabled'}
                        color={value === true ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
    ];

    const handleView = (device: any) => {
        setSelectedDevice(device);
        setShowEditModal(true);
    };

    return (
        <Grid
            sx={{
                width: '100%',
                height: isMobile || isIpad ? '100%' : '80%',
                justifyContent: 'left',
                alignItems: 'center',
            }}
        >
            <Grid
                sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    GPS Devices
                </Typography>
            </Grid>

            <Grid
                sx={{
                    textAlign: 'right',
                    paddingBottom: '10px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: isMobile ? '0' : '10px',
                }}
            >
                {devices && devices.length > 0 && (
                    <CustomSwitch
                        checked={mapView}
                        onChange={() => setMapView(!mapView)}
                        label={
                            <Grid
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <MapIcon sx={{ fontSize: 18 }} />
                                <Typography sx={{ fontSize: '14px' }}>
                                    Map View
                                </Typography>
                            </Grid>
                        }
                    />
                )}

                {isMobile ? (
                    <CustomIconButton
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            setSelectedDevice(undefined);
                            setShowModal(true);
                        }}
                        sx={{
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'secondary',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                    >
                        <AddCircleIcon />
                    </CustomIconButton>
                ) : (
                    <CustomButton
                        variant='contained'
                        color='primary'
                        startIcon={<AddCircleIcon />}
                        onClick={() => {
                            setSelectedDevice(undefined);
                            setShowModal(true);
                        }}
                    >
                        Add Device
                    </CustomButton>
                )}
            </Grid>

            <Grid
                sx={{
                    width: '100%',
                    minHeight: isMobile || isIpad ? '65%' : '100%',
                    justifyContent: 'left',
                    paddingBottom: 10,
                }}
            >
                {mapView ? (
                    <APIProvider
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                    >
                        {' '}
                        <DeviceMapView />{' '}
                    </APIProvider>
                ) : (
                    <GenericTable
                        columns={columns}
                        data={devices || []}
                        tableName='Devices'
                        loading={isLoading}
                        pagination={{ pageSize: 50, pageIndex: 0 }}
                        onRowClick={(row) => handleView(row)}
                        cursor='pointer'
                    />
                )}
            </Grid>

            <DeviceModal open={showModal} onClose={() => setShowModal(false)} />
            <DeviceModal
                open={showEditModal}
                onClose={() => setShowEditModal(false)}
                selectedDevice={selectedDevice}
            />
        </Grid>
    );
}
